import '@rainbow-me/rainbowkit/styles.css';
import { RainbowKitProvider, getDefaultWallets } from '@rainbow-me/rainbowkit';
import { configureChains, createConfig, WagmiConfig, useAccount } from 'wagmi';
import { base } from 'wagmi/chains';
import { QueryClient, QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import { publicProvider } from 'wagmi/providers/public';
import { useEffect, useState } from 'react';
// (Wallet list handled by getDefaultWallets)

// Suppress Chrome extension errors globally
if (typeof window !== 'undefined') {
  const originalError = window.onerror;
  window.onerror = function(message, source, lineno, colno, error) {
    if (typeof message === 'string' && message.includes('chrome.runtime.sendMessage')) {
      console.log('✅ Extension error suppression active');
      return true;
    }
    if (originalError) {
      return originalError.call(this, message, source, lineno, colno, error);
    }
    return false;
  };
}

// ⚠️ CRITICAL: Configure ONLY Base Mainnet (Chain ID 8453)
const { chains, publicClient, webSocketPublicClient } = configureChains(
  [base],
  [publicProvider()]
);

console.log('🟢 LEXI Oracle configured for Base Mainnet (Chain ID: 8453)');
console.log('🟢 Network:', base.name);
console.log('🟢 RPC:', base.rpcUrls.default.http[0]);

// Get WalletConnect project ID
const projectId = process.env.REACT_APP_WALLETCONNECT_PROJECT_ID || '88bbb5c3-9a4d-4a8b-81b9-0490fe32d750';

// Use RainbowKit defaults which handle mobile deep links & WalletConnect properly
const { connectors } = getDefaultWallets({
  appName: 'LEXI Oracle',
  projectId,
  chains,
});

console.log('🟢 Wallet connectors configured:', connectors.length);

const config = createConfig({
  autoConnect: false,
  connectors,
  publicClient,
  webSocketPublicClient,
});


const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: true, // Enable to detect wallet connection changes
      retry: false,
      staleTime: 0, // Always refetch to detect connection state
    },
  },
});

// Component to watch connection state and refetch on change
function ConnectionWatcher({ children }: { children: React.ReactNode }) {
  const { isConnected, address } = useAccount();
  const queryClient = useQueryClient();
  const [prevConnected, setPrevConnected] = useState(false);

  useEffect(() => {
    // If connection state changed from disconnected to connected, invalidate queries
    if (isConnected && !prevConnected && address) {
      console.log('✅ Wallet connected:', address);
      // Invalidate all queries to force refetch
      queryClient.invalidateQueries();
      // Small delay to ensure modal detects the change
      setTimeout(() => {
        queryClient.invalidateQueries();
      }, 500);
    }
    setPrevConnected(isConnected);
  }, [isConnected, address, prevConnected, queryClient]);

  return <>{children}</>;
}

export function Providers({ children }: { children: React.ReactNode }) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    
    if (typeof window !== 'undefined') {
      console.log('🔍 Checking wallet availability...');
      
      // Check for MetaMask specifically
      if (window.ethereum?.isMetaMask) {
        console.log('✅ MetaMask detected');
      }
      
      // Check for Rainbow specifically
      if (window.ethereum?.isRainbow) {
        console.log('✅ Rainbow detected');
      }
      
      // Check for Coinbase
      if (window.ethereum?.isCoinbaseWallet) {
        console.log('✅ Coinbase Wallet detected');
      }
      
      // Check what's actually injected
      if (window.ethereum) {
        console.log('🔍 Active provider:', 
          window.ethereum.isMetaMask ? 'MetaMask' : 
          window.ethereum.isRainbow ? 'Rainbow' : 
          window.ethereum.isCoinbaseWallet ? 'Coinbase' : 
          'Unknown'
        );
      }

    }
  }, []);

  if (!mounted) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        flexDirection: 'column',
        gap: '20px',
        backgroundColor: '#000',
        color: '#0f0',
        fontFamily: 'monospace'
      }}>
        <div style={{ fontSize: '48px' }}>🤖</div>
        <div style={{ fontSize: '20px' }}>Initializing LEXI Oracle...</div>
        <div style={{ fontSize: '14px', opacity: 0.7 }}>Base Mainnet • Chain ID: 8453</div>
      </div>
    );
  }

  return (
    <WagmiConfig config={config}>
      <QueryClientProvider client={queryClient}>
        <RainbowKitProvider 
          chains={chains} 
          modalSize="compact"
          coolMode={false}
          showRecentTransactions={false}
        >
          <ConnectionWatcher>
            {children}
          </ConnectionWatcher>
        </RainbowKitProvider>
      </QueryClientProvider>
    </WagmiConfig>
  );
}